import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertCircle, Sparkles } from "lucide-react";
import axios from "axios";
import { useSubscription } from "../context/SubscriptionContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useSubscription();
  const [state, setState] = useState("polling"); // polling | success | expired | error
  const [message, setMessage] = useState("Confirming your payment…");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      setMessage("Missing session id.");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const r = await axios.get(`${BACKEND}/api/checkout/status/${sessionId}`);
        const d = r.data;
        if (d.payment_status === "paid") {
          setState("success");
          setMessage("Welcome to FreeFitFuel Pro.");
          try { await refresh(); } catch {}
          return;
        }
        if (d.status === "expired") {
          setState("expired");
          setMessage("This checkout session has expired. Please try again.");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setState("error");
          setMessage("Still processing — your subscription will activate shortly. Refresh in a minute.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts >= MAX_POLLS) {
          setState("error");
          setMessage("We couldn't verify your payment. Contact support if you were charged.");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div
      className="min-h-screen bg-[#0b0b0c] text-white grid place-items-center px-6"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="payment-success-screen"
    >
      <div className="w-full max-w-sm text-center">
        {state === "polling" && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-zinc-900 border border-zinc-800 grid place-items-center">
              <Loader2 className="h-7 w-7 text-lime-400 animate-spin" />
            </div>
            <h1 className="mt-5 text-xl font-semibold">Confirming…</h1>
            <p className="mt-2 text-sm text-zinc-400">{message}</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-lime-400 to-emerald-500 grid place-items-center shadow-2xl shadow-lime-400/40">
              <Check className="h-10 w-10 text-black" strokeWidth={3} />
            </div>
            <div className="mt-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-lime-400/10 text-lime-300 text-xs font-medium border border-lime-400/30">
              <Sparkles className="h-3.5 w-3.5" /> Pro active
            </div>
            <h1 className="mt-4 text-2xl font-bold">{message}</h1>
            <p className="mt-2 text-sm text-zinc-400">Your full plan, AI tools, and 1167-exercise library are unlocked.</p>
            <Link
              to="/my-plan"
              data-testid="payment-success-continue"
              className="mt-6 inline-flex items-center justify-center w-full rounded-full h-12 bg-lime-400 text-black font-semibold gap-2"
            >
              Open my plan <Sparkles className="h-4 w-4" />
            </Link>
          </>
        )}
        {(state === "expired" || state === "error") && (
          <>
            <div className="mx-auto h-16 w-16 rounded-full bg-zinc-900 border border-amber-500/30 grid place-items-center">
              <AlertCircle className="h-7 w-7 text-amber-400" />
            </div>
            <h1 className="mt-5 text-xl font-semibold">Hmm — {state === "expired" ? "session expired" : "couldn't verify"}</h1>
            <p className="mt-2 text-sm text-zinc-400">{message}</p>
            <Link
              to="/my-plan"
              data-testid="payment-success-back"
              className="mt-6 inline-flex items-center justify-center w-full rounded-full h-12 bg-zinc-800 text-zinc-100 font-semibold"
            >
              Back
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
